import { useState } from "react";
import { useLocation, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import Sidebar from "@surgia/ui/containers/sidebar";
import dashboardIcon from "@surgia/ui/icons/sidebar/dashboard.svg";
import tenantsIcon from "@surgia/ui/icons/sidebar/tenants.svg";
import purchaseIcon from "@surgia/ui/icons/sidebar/purchase.svg";
import usersIcon from "@surgia/ui/icons/sidebar/users.svg";
import catalogIcon from "@surgia/ui/icons/sidebar/catalog.svg";
import { SidebarItemType } from "@surgia/ui/components/sidebar/sidebarNavItem/constants";
import { useAuth } from "react-oidc-context";

interface LayoutProps {}

const Layout: React.FC<LayoutProps> = ({}) => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const auth = useAuth();

  // Get auth state from Redux
  const authState = useSelector((state: RootState) => state.auth);
  // Check if user is authenticated based on global state
  const isAuthenticated =
    authState.globalState.access_token && authState.globalState.subject_id;

  // Create user profile from Redux global state instead of useAuth
  const userProfile = isAuthenticated
    ? {
        name:
          authState.globalState.full_name ||
          `${authState.globalState.first_name || ""} ${authState.globalState.last_name || ""}`.trim(),
        email: authState.globalState.email,
        first_name: authState.globalState.first_name,
        last_name: authState.globalState.last_name,
        full_name: authState.globalState.full_name,
        subject_id: authState.globalState.subject_id,
      }
    : null;

  const navigation = [
    {
      type: "link" as SidebarItemType,
      href: "/",
      icon: dashboardIcon,
      label: t("shared.sidebar.dashboard"),
      isActive: location.pathname === "/",
    },
    {
      type: "link" as SidebarItemType,
      href: "/tenants",
      icon: tenantsIcon,
      label: t("shared.sidebar.tenants"),
      isActive: location.pathname === "/tenants",
    },
    {
      type: "link" as SidebarItemType,
      href: "/users",
      icon: usersIcon,
      label: t("shared.sidebar.users"),
      isActive: location.pathname === "/users",
    },
    {
      type: "link" as SidebarItemType,
      href: "/catalog",
      icon: catalogIcon,
      label: t("shared.sidebar.catalog"),
      isActive: location.pathname === "/catalog",
    },
    {
      type: "link" as SidebarItemType,
      href: "/purchase-orders",
      icon: purchaseIcon,
      label: t("shared.sidebar.purchase"),
      isActive: location.pathname === "/purchase-orders",
    },
  ];

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  const handleSignOut = () => {
    try {
      // Broadcast logout to other tabs
      const authChannel = new BroadcastChannel("auth-sync");
      authChannel.postMessage({ type: "LOGOUT" });
      authChannel.close();

      // Clear local storage and session storage
      localStorage.clear();
      sessionStorage.clear();

      // Remove user and redirect
      auth.removeUser();
      auth.signoutRedirect();
    } catch (error) {
      console.error("Error during sign out:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-1" dir={isRTL ? "rtl" : "ltr"}>
      <div className="flex h-[calc(100vh-0.5rem)] bg-white rounded-lg overflow-hidden">
        {/* Sidebar Container - only show if authenticated */}
        {isAuthenticated && (
          <div className="p-1 bg-gray-100 transition-all duration-300 ease-in-out">
            <Sidebar
              className="rounded-lg h-[99.9%]"
              isCollapsed={isSidebarCollapsed}
              onToggleCollapse={toggleSidebar}
              items={navigation}
              userProfile={userProfile}
              onSignOut={handleSignOut}
            />
          </div>
        )}

        {/* Main content - always render */}
        <div className="flex-1 overflow-auto bg-white transition-all duration-300 ease-in-out">
          <main className="px-5 pt-5 h-full">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;

